import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { BRAND_NAME } from "@/config";
import { useSiteBook } from "./SiteBookModal";

const TABS = [
  { href: "/", label: "Home", key: "home" },
  { href: "/solutions", label: "Solutions", key: "solutions" },
  { href: "/how-it-works", label: "How It Works", key: "how-it-works" },
  { href: "/pricing", label: "Pricing", key: "pricing" },
  { href: "/testimonials", label: "Testimonials", key: "testimonials" },
  { href: "/blog", label: "Blog", key: "blog" },
  { href: "/contact", label: "Contact", key: "contact" },
];

const Logo = () => (
  <a href="/" className="flex items-center gap-1.5" data-testid="nav-logo">
    <img src="/slammed-icon.png?v=5" alt={`${BRAND_NAME} logo`} width="32" height="32" className="h-8 w-8 object-contain" />
    <span className="font-display text-xl font-extrabold tracking-tight text-gradient">{BRAND_NAME}</span>
  </a>
);

export default function SiteNav({ active = "home" }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openBook } = useSiteBook();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-40 transition-all duration-300 ${
        scrolled || open ? "bg-white/95 border-b border-navy/5 shadow-[0_8px_30px_-16px_rgba(14,18,48,0.25)]" : ""
      }`}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5 sm:px-8">
        <Logo />
        <nav className="hidden items-center gap-1 md:flex" data-testid="site-nav-tabs">
          {TABS.map((t) => (
            <a
              key={t.key}
              href={t.href}
              data-testid={`nav-tab-${t.key}`}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                active === t.key ? "bg-brand-indigo/10 text-brand-indigo" : "text-navy/60 hover:text-navy"
              }`}
            >
              {t.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openBook}
            data-testid="nav-book-call"
            className="sl-glow-cta hidden cursor-pointer rounded-full bg-brand-gradient px-5 py-2.5 font-display text-sm font-semibold text-white shadow-pill transition-transform duration-300 hover:-translate-y-0.5 sm:inline-flex"
          >
            Book a free call
          </button>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(!open)}
            className="rounded-full p-2 text-navy/60 hover:bg-navy/5 md:hidden"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-navy/5 bg-white px-5 py-3 md:hidden" data-testid="site-nav-mobile">
          {TABS.map((t) => (
            <a
              key={t.key}
              href={t.href}
              data-testid={`nav-tab-${t.key}`}
              className={`block rounded-xl px-4 py-3 text-sm font-semibold ${
                active === t.key ? "bg-brand-indigo/10 text-brand-indigo" : "text-navy/70"
              }`}
            >
              {t.label}
            </a>
          ))}
          <button
            type="button"
            onClick={() => { setOpen(false); openBook(); }}
            data-testid="nav-book-call-mobile"
            className="mt-2 block w-full rounded-xl bg-brand-gradient px-4 py-3 text-center text-sm font-bold text-white"
          >
            Book a free call
          </button>
        </nav>
      )}
    </header>
  );
}
